/**
 * MenuCatalog — category pills, search, product/combo grid, desktop cart sidebar.
 * Manages internally: selectedCategory, searchQuery, sectionRefs, scroll observer.
 */
import { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Search, ShoppingBag, Plus, Minus, X } from "lucide-react";
import { toast } from "sonner";
import { BannerCarousel, ProductCard } from "@/components/public/MenuComponents";
import { resolveMedia } from "@/utils/mediaUtils";
import { findProductBySlug, slugify, buildProductSlug } from "@/utils/productSlug";
import { isEligibleForSmartMenuDiscount } from "@/utils/smartPricingUtils";
import { trackPixelEvent } from "@/hooks/useAnalytics";
import { trackAddToCart } from "@/utils/tracking";

function contrastText(bgHex) {
  const h = (bgHex || "#ffffff").replace("#", "");
  const r = parseInt(h.substring(0, 2), 16), g = parseInt(h.substring(2, 4), 16), b = parseInt(h.substring(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.75 ? "#111827" : "#ffffff";
}

export function MenuCatalog({
  tc, isDark, cardBg, cardBorder, catColor, priceColor,
  categories, products, combos,
  cart, setCart, cartTotal, cartCount, addToCart, addToCartSilent, updateQuantity,
  suggestions, trackUpsell,
  openCheckout,
  smartPricing, restaurant, fmt,
  highlights, deepLinkSlug, suppressAutoOpen = false,
  searchBanners = [],
}) {
  // Build lookup: product_id → badge type ("best_seller" | "recommended" | "premium")
  const highlightMap = {};
  if (highlights) {
    if (highlights.best_seller) highlightMap[highlights.best_seller] = "best_seller";
    if (highlights.recommended) highlightMap[highlights.recommended] = "recommended";
    if (highlights.premium)     highlightMap[highlights.premium]     = "premium";
  }
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const sectionRefs = useRef({});

  // Build ID-anchored slug map: productId → "slug--productId" (guaranteed unique)
  const productSlugMap = useMemo(() => {
    const map = {};
    products.forEach(p => { map[p.id] = buildProductSlug(p); });
    return map;
  }, [products]);

  // Deep link: find the target product to auto-open its modal
  const deepLinkProduct = deepLinkSlug ? findProductBySlug(products, deepLinkSlug) : null;
  const restaurantSlug = restaurant?.slug || '';

  // [DEEP_LINK_DIAG_V3] diagnostic logs — remove after confirming deep links work
  useEffect(() => {
    if (!products.length) return;
    console.log('[DEPLOY_VERSION_DEEP_LINKS_V3_ACTIVE]');
    console.log('[DEEP_LINK_PRODUCTS_COUNT]', products.length);
    console.table(products.map(p => ({ id: p.id || p._id, name: p.name, slug: slugify(p.name), urlSlug: buildProductSlug(p) })));
    if (!deepLinkSlug) return;
    console.log('[DEEP_LINK_SLUG_EXTRACTED]', deepLinkSlug);
    console.log('[DEEP_LINK_RESTAURANT_SLUG]', restaurant?.slug || '(not set)');
    const match = findProductBySlug(products, deepLinkSlug);
    console.log('[DEEP_LINK_MATCH_RESULT]', match ? { id: match.id, name: match.name } : 'NOT FOUND');
  }, [deepLinkSlug, products.length]); // eslint-disable-line

  const sortedCategories = [...categories].sort((a, b) => (a.position ?? 999) - (b.position ?? 999));
  const categoriesWithProducts = sortedCategories.filter(cat => products.some(p => p.category_id === cat.id));
  const filteredProducts = products.filter(p =>
    !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase()) || (p.description || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  const scrollToCategory = (catId) => {
    setSelectedCategory(catId);
    const el = sectionRefs.current[catId];
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 120, behavior: "smooth" });
  };

  useEffect(() => {
    if (categories.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach(entry => { if (entry.isIntersecting) setSelectedCategory(entry.target.dataset.catid); }); },
      { rootMargin: "-130px 0px -60% 0px", threshold: 0 }
    );
    const tid = setTimeout(() => {
      Object.entries(sectionRefs.current).forEach(([catId, el]) => { if (el) { el.dataset.catid = catId; observer.observe(el); } });
    }, 500);
    return () => { clearTimeout(tid); observer.disconnect(); };
  }, [categories, products]);

  return (
    <>
      {/* Category pills */}
      <div id="menu-section" className="sticky top-[52px] sm:top-[56px] z-40 border-b shadow-sm" style={{ backgroundColor: tc.bg, borderColor: `${tc.text}10` }}>
        <div className="max-w-5xl mx-auto px-3 sm:px-4">
          <div data-touch-pan-x="" className="flex gap-1.5 overflow-x-auto py-2 scrollbar-hide">
            {categoriesWithProducts.map(cat => (
              <button key={cat.id} onClick={() => { scrollToCategory(cat.id); setSearchQuery(""); }}
                className="whitespace-nowrap flex-shrink-0 px-3 sm:px-4 py-1.5 rounded-full text-[11px] sm:text-xs font-semibold transition-all border"
                style={selectedCategory === cat.id
                  ? { backgroundColor: catColor, color: contrastText(catColor), borderColor: catColor }
                  : { backgroundColor: tc.headerBg, color: contrastText(tc.headerBg), borderColor: `${contrastText(tc.headerBg)}20` }}
                data-testid={`cat-btn-${cat.id}`}>{cat.name.toUpperCase()}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="max-w-5xl mx-auto px-3 sm:px-4 py-2 sm:py-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: isDark ? 'rgba(255,255,255,0.4)' : '#9ca3af' }} />
          <Input value={searchQuery} onChange={e => { setSearchQuery(e.target.value); if (e.target.value) setSelectedCategory(null); }} placeholder={t('menu.searchMenu')} className="pl-9 rounded-xl h-9 sm:h-10 text-sm" style={{ backgroundColor: cardBg, borderColor: cardBorder, color: tc.text }} data-testid="menu-search" />
          {searchQuery && (
            <button onClick={() => { setSearchQuery(""); setSelectedCategory(categories[0]?.id); }} className="absolute right-3 top-1/2 -translate-y-1/2"><X className="w-4 h-4" style={{ color: isDark ? 'rgba(255,255,255,0.4)' : '#9ca3af' }} /></button>
          )}
        </div>
      </div>

      {searchBanners.some(banner => typeof banner === "string" && banner.trim()) && (
        <div className="max-w-5xl mx-auto px-3 sm:px-4 pb-2" data-testid="menu-search-banners">
          <BannerCarousel banners={searchBanners} compact />
        </div>
      )}

      {/* Products + Cart sidebar */}
      <div className="max-w-5xl mx-auto px-3 sm:px-4">
        <div className="flex gap-6">
          <div className="flex-1 pb-28 sm:pb-32">
            {searchQuery && <p className="text-sm mb-4" style={{ color: `${tc.text}80` }}>{t("publicMenu.searchResults", { count: filteredProducts.length, query: searchQuery })}</p>}
            {combos.length > 0 && !searchQuery && (
              <div className="mb-6">
                <h3 className="text-sm font-bold uppercase tracking-wider mb-3" style={{ color: `${tc.text}99` }}>{t("publicMenu.combosOffers")}</h3>
                <div className="grid grid-cols-1 gap-2">
                  {combos.map(combo => {
                    const productNamesLine = (combo.products || []).map(p => p.name).filter(Boolean).join(" + ");
                    const showDesc = combo.description && combo.description.trim() !== productNamesLine;
                    return (
                      <button key={combo.id} className="rounded-xl border p-3 text-left hover:shadow-md transition" style={{ backgroundColor: isDark ? 'rgba(255,165,0,0.08)' : '#fff7ed', borderColor: isDark ? 'rgba(255,165,0,0.3)' : '#fed7aa' }}
                        onClick={() => {
                          setCart(prev => [...prev, { id: combo.id, cartId: `combo_${combo.id}`, name: combo.name, price: combo.combo_price, originalPrice: combo.original_price, quantity: 1, isCombo: true, comboProducts: combo.products }]);
                          toast.success(t("publicMenu.comboAdded", { name: combo.name }));
                          const eventId = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `atc_${Date.now()}`;
                          trackPixelEvent("AddToCart", { content_ids: [combo.id], content_name: combo.name, value: combo.combo_price, currency: restaurant?.currency || "EUR" }, { eventID: eventId });
                          trackAddToCart({ product_id: combo.id, cart_value: combo.combo_price, metadata: { product_name: combo.name, event_id: eventId, source: "combo" } });
                        }}
                        data-testid={`combo-${combo.id}`}>
                        <div className="flex items-center gap-3">
                          {combo.image_url && <img src={combo.image_url} alt={combo.name} className="w-16 h-16 rounded-lg object-cover shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <span className="font-bold text-sm" style={{ color: tc.text }}>{combo.name}</span>
                            {showDesc && <p className="text-xs mt-0.5" style={{ color: `${tc.text}99` }}>{combo.description}</p>}
                            {productNamesLine && <p className="text-xs mt-0.5" style={{ color: `${tc.text}77` }}>{productNamesLine}</p>}
                            <div className="flex items-center gap-2 mt-1.5">
                              <span className="text-xs line-through" style={{ color: `${tc.text}55` }}>{fmt(combo.original_price || 0)}</span>
                              <span className="font-bold text-orange-600">{fmt(combo.combo_price || 0)}</span>
                              {combo.savings > 0 && <span className="text-[10px] bg-red-500 text-white px-1.5 py-0.5 rounded-full font-bold">-{fmt(combo.savings)}</span>}
                            </div>
                          </div>
                          <div className="w-9 h-9 rounded-full bg-orange-500 text-white flex items-center justify-center shrink-0 active:scale-95 transition-transform"><Plus className="w-4 h-4" /></div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {categoriesWithProducts.map(cat => {
              const catProducts = filteredProducts.filter(p => p.category_id === cat.id);
              if (catProducts.length === 0) return null;
              return (
                <div key={cat.id} ref={el => sectionRefs.current[cat.id] = el} className="mb-8" data-testid={`section-${cat.id}`}>
                  <h2 className="text-lg sm:text-xl font-bold mb-1" style={{ color: tc.text }}>{cat.name}</h2>
                  {cat.description && <p className="text-sm mb-3" style={{ color: `${tc.text}99` }}>{cat.description}</p>}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid={`product-grid-${cat.id}`}>
                    {catProducts.map(product => {
                      const productDiscount = (smartPricing?.active && isEligibleForSmartMenuDiscount(product, cat.name))
                        ? smartPricing.discount
                        : 0;
                      return (
                        <ProductCard key={product.id} product={product} onAdd={addToCart} discount={productDiscount} themeColor={tc.primary} priceColor={priceColor} themeBg={tc.bg} themeText={tc.text} currency={restaurant?.currency || "EUR"} highlight={highlightMap[product.id] || null} locale={restaurant?.locale || "es"} autoOpen={!suppressAutoOpen && deepLinkProduct?.id === product.id} openCheckout={openCheckout} restaurantSlug={restaurantSlug} productUrlSlug={productSlugMap[product.id] || slugify(product.name)} />
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {filteredProducts.length === 0 && (
              <div className="text-center py-16"><Search className="w-10 h-10 text-gray-300 mx-auto mb-3" /><p className="text-gray-400 text-sm">{t("menu.noProductsFound")}</p></div>
            )}
          </div>

          {/* Desktop Cart Sidebar */}
          <div className="hidden lg:block w-80 flex-shrink-0">
            <div className="sticky top-[120px] rounded-xl border shadow-sm overflow-hidden" style={{ backgroundColor: cardBg, borderColor: cardBorder }}>
              <div className="p-4" style={{ backgroundColor: tc.primary }}><h3 className="font-bold text-white flex items-center gap-2"><ShoppingBag className="w-4 h-4" /> {t('cart.yourOrder')}</h3></div>
              {cart.length === 0 ? (
                <div className="p-6 text-center"><ShoppingBag className="w-8 h-8 mx-auto mb-2" style={{ color: isDark ? 'rgba(255,255,255,0.2)' : '#e5e7eb' }} /><p className="text-sm" style={{ color: isDark ? 'rgba(255,255,255,0.4)' : '#9ca3af' }}>{t('cart.empty')}</p></div>
              ) : (
                <>
                  <div className="p-4 space-y-3 max-h-[300px] overflow-y-auto">
                    {cart.map(item => (
                      <div key={item.cartId || item.id} className="flex items-center justify-between text-sm" data-testid={`cart-item-${item.cartId || item.id}`}>
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <div className="flex items-center gap-1 rounded-lg p-0.5" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : '#f3f4f6' }}>
                            <button onClick={() => updateQuantity(item.product_id || item.cartId || item.id, -1)} className="w-6 h-6 rounded-md flex items-center justify-center" style={{ color: tc.text }}><Minus className="w-3 h-3" /></button>
                            <span className="w-5 text-center font-semibold text-xs" style={{ color: tc.text }}>{item.quantity}</span>
                            <button onClick={() => updateQuantity(item.product_id || item.cartId || item.id, 1)} className="w-6 h-6 rounded-md flex items-center justify-center" style={{ color: tc.primary }}><Plus className="w-3 h-3" /></button>
                          </div>
                          <span className="truncate text-xs" style={{ color: isDark ? 'rgba(255,255,255,0.7)' : '#374151' }}>{item.name}</span>
                        </div>
                        <span className="font-semibold ml-2 text-xs" style={{ color: tc.text }}>{fmt(item.price * item.quantity)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="p-4 border-t space-y-2" style={{ borderColor: cardBorder }}>
                    {suggestions.length > 0 && (
                      <div className="mb-2" data-testid="sidebar-upsell">
                        <p className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: tc.primary }}>Adicione tambem</p>
                        <div className="space-y-1">
                          {suggestions.filter(s => !cart.some(c => c.id === s.id)).slice(0, 2).map(s => (
                            <button key={s.id} onClick={() => { trackUpsell("accepted", "sidebar", s); addToCartSilent(s); }} className="w-full flex items-center gap-2 p-1.5 rounded-lg border border-gray-100 hover:border-gray-200 transition text-left group" data-testid={`sidebar-suggestion-${s.id}`}>
                              {s.image_url ? <img src={resolveMedia(s.image_url)} alt={s.name} className="w-8 h-8 rounded-md object-cover shrink-0" /> : <div className="w-8 h-8 rounded-md shrink-0 flex items-center justify-center text-sm" style={{ backgroundColor: `${tc.primary}10` }}>🍽️</div>}
                              <span className="flex-1 text-[11px] text-gray-700 truncate">{s.name}</span>
                              <span className="text-[11px] font-bold shrink-0" style={{ color: tc.primary }}>+{fmt(s.price)}</span>
                              <div className="w-5 h-5 rounded-full text-white flex items-center justify-center shrink-0" style={{ backgroundColor: tc.primary }}><Plus className="w-3 h-3" /></div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex justify-between text-sm"><span className="text-gray-500">{t('cart.subtotal')}</span><span>{fmt(cartTotal)}</span></div>
                    <Separator />
                    <div className="flex justify-between font-bold text-lg"><span>{t('cart.total')}</span><span style={{ color: tc.primary }}>{fmt(cartTotal)}</span></div>
                    <Button onClick={openCheckout} className="w-full h-11 text-white font-bold rounded-xl" style={{ backgroundColor: tc.primary }} data-testid="sidebar-checkout-btn">{t('cart.checkout')}</Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
